<template>
  <div
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDropFile"
    @dragover.prevent
    class="file-dropzone absolute inset-0 h-full w-full"
    :class="{
      'pointer-events-auto': isDraggingGlobal && !props.alwaysVisible,
      'pointer-events-none': !isDraggingGlobal && !props.alwaysVisible
    }"
  >
    <!-- Visual drop area when file is being hovered -->
    <div
      v-if="isDraggingGlobal || props.alwaysVisible"
      :class="[
        'relative flex h-full w-full items-center justify-center rounded-md text-center',
        { 'pointer-events-none': isDraggingGlobal },
        invalidTypeHover || multipleFilesHover
          ? 'outline-error outline-2'
          : isFileHover
            ? 'outline-secondary outline-2'
            : 'outline-gray-lighten-1 outline-2 outline-dashed!'
      ]"
    >
      <div
        v-if="invalidTypeHover || multipleFilesHover"
        class="surface pointer-events-none absolute inset-0 flex items-center justify-center rounded-md"
      >
        <span class="text-error" v-if="invalidTypeHover">Dit bestandstype is niet toegestaan</span>
        <span class="text-error" v-if="multipleFilesHover">
          Meerdere bestanden zijn niet toegestaan
        </span>
      </div>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useFileUpload } from "@/composables/file/file-upload"
import { FileRelation } from "@/models/file/file-relation.model"
import ModelManager from "@/services/model-manager"
import { useDragStore } from "@/store/drag-store"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import type { IBase } from "@/interfaces/api/base.interface"
import type { ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { FileBucket } from "@/models/file-bucket.model"
import { FileController } from "@/controllers/file-controller"
import { FileHelper } from "@/helper/file-helper.ts"

const props = withDefaults(
  defineProps<{
    /**
     * The context in which the file uploader is used.
     */
    context: FileUploadContexts

    /**
     * Whether the file uploader should be standalone or not. If true, uploaded files will not be attached to a model.
     */
    standalone?: boolean

    /**
     * Accept attribute for file input element.
     */
    accept?: string

    /**
     * The model to which the files will be attached.
     */
    fileBucket?: FileBucket

    /**
     * Whether or not to manage the uploaded files using ModelManager (see ModelManager for more info).
     */
    managed?: boolean

    /**
     * Whether to allow multiple files to be uploaded.
     */
    multiple?: boolean

    /**
     * Whether the dropzone is disabled.
     */
    disabled?: boolean

    /**
     * Whether the dropzone should always be visible, overriding the isDraggingGlobal condition.
     */
    alwaysVisible?: boolean
  }>(),
  {
    standalone: false,
    managed: true,
    multiple: true,
    alwaysVisible: false
  }
)

const emit = defineEmits([
  "fileDrop",
  "urlDrop",
  "isUploading",
  "fileUploaded",
  "fileLinked",
  "isFileHover",
  "multipleFilesHover"
])

const isFileHover = ref(false)
const invalidTypeHover = ref(false)
const multipleFilesHover = ref(false)

/**
 * Since the dropzone overlaps the parent component, pointer events (click, hover, etc.) will stop working.
 * Therefore, pointer-events-none is applied. But this also means that the dropzone will not receive any
 * drag events. We solve this by setting the pointer-events-auto class when the user is dragging a file.
 */
const dragStore = useDragStore()
const { isDraggingGlobal, fileRelationBeingDragged } = storeToRefs(dragStore)

const { upload, uploadAndCreateRelation, isUploading, filesAreValid, displayErrorDialog } =
  useFileUpload(props.managed, {
    accept: props.accept
  })

async function onDropFile(event: any) {
  if (!isFileHover.value || props.disabled) {
    return // File dropping is not allowed here.
  }

  emit("fileDrop", event)

  const files = Array.from<File>(event.dataTransfer?.files)
  const intention = event.dataTransfer?.getData("intention")

  // Check for multiple files before resetting hover state
  if (!props.multiple && files.length > 1) {
    // Reset all hover states immediately
    isFileHover.value = false
    multipleFilesHover.value = false
    invalidTypeHover.value = false
    return
  }

  isFileHover.value = false
  multipleFilesHover.value = false

  // If we still have no File objects or file relation being dragged, check for a dragged-in URL/HTML
  if (files.length === 0 && !fileRelationBeingDragged.value) {
    // Gmail and most cross-origin drags expose a text/uri-list or text/html
    const url =
      event.dataTransfer.getData("text/uri-list")?.trim() ||
      // fallback to HTML snippet and extract src if needed
      event.dataTransfer.getData("text/html")?.match(/src="([^"]+)"/)?.[1]

    // Check if URL is valid (Whatsapp will drop an invalid string like
    // blob:https://web.whatsapp.com/dcb09bb8-2730-4617-807c-ecfff5fa1576
    if (url && !/^https?:\/\//i.test(url)) {
      // If the URL does not start with http:// or https://, it is not a valid URL
      console.warn("Invalid URL dropped:", url)
      return
    }

    if (!url) {
      // No files or URLs were dropped, exit early
      return
    }

    try {
      // If we have a URL, we can download the file from it
      isUploading.value = true
      const file = await FileHelper.downloadFileFromURL(url)
      files.push(file)
    } catch {
      // Emit the URL to the parent component (probably a HTML file or CORS issue).
      isUploading.value = false
      emit("urlDrop", url)
    }
  }

  if (files && files.length > 0) {
    await handleFilesDrop(files)
  } else if (fileRelationBeingDragged.value) {
    handleFileRelationDrop(fileRelationBeingDragged.value, intention)
  }
}

async function handleFilesDrop(files: File[]) {
  if (!filesAreValid(files)) {
    isUploading.value = false
    displayErrorDialog()
    return
  }

  if (props.standalone) {
    await upload(files, props.context, onFileUploaded)
  } else if (props.fileBucket) {
    await uploadAndCreateRelation(files, props.fileBucket, props.context, onFileUploaded)
  } else {
    throwNoFileBucketError()
  }
}

const fileController: FileController = inject("fileController")!
function handleFileRelationDrop(fileRelation: FileRelation, intention: string) {
  const newFileRelation = ModelManager.getInstance().create(
    fileRelation.constructor as ModelConstructorSignature<IBase, FileRelation>
  )
  newFileRelation.file = fileRelation.file
  if (props.standalone) {
    onFileUploaded(newFileRelation)
  } else if (props.fileBucket) {
    fileController.linkFile(newFileRelation.file, props.fileBucket, props.context)
    onFileLinked(newFileRelation)
  } else {
    throwNoFileBucketError()
  }

  // If the intention is to move the file, delete the original file relation.
  if (intention === "move") {
    fileController.deleteFileRelation(fileRelation)

    // Notify the drag store that the move was successful so the drag source can emit the moved event.
    dragStore.notifyMoveSuccesful()
  }
}

function onDragEnter(e: DragEvent) {
  if (props.disabled) {
    return
  }

  invalidTypeHover.value = false // Reset invalid type state
  multipleFilesHover.value = false // Reset multiple files state

  if (e.dataTransfer && e.dataTransfer.items && e.dataTransfer.items.length > 0) {
    const firstItemKind = e.dataTransfer.items[0].kind

    if (firstItemKind === "file") {
      // Check if multiple files are being dragged and multiple is not allowed
      if (!props.multiple && e.dataTransfer.items.length > 1) {
        multipleFilesHover.value = true
        isFileHover.value = true
        return
      }

      if (props.accept && e.dataTransfer.items[0].type) {
        const acceptedTypes = props.accept.split(",").map((type) => type.trim())
        const isValidType = acceptedTypes.some((type) => {
          if (type.endsWith("/*")) {
            const baseType = type.slice(0, -2)
            return e.dataTransfer?.items[0].type.startsWith(baseType)
          }
          return e.dataTransfer?.items[0].type === type
        })
        isFileHover.value = isValidType
        if (!isValidType) {
          invalidTypeHover.value = true
        }
      } else {
        isFileHover.value = true
      }
    } else if (firstItemKind === "string") {
      /* Handling string items (i.e.)  */
      const fileRelation = fileRelationBeingDragged.value

      // Check if fileRelation exists
      if (fileRelation) {
        // In standalone mode, always set isFileHover to true
        if (props.standalone) {
          isFileHover.value = true
        }
        // In non-standalone mode, check if the file is already related
        else {
          const isFileAlreadyRelated = props.fileBucket?.fileRelations
            .map((fr) => fr.file.id)
            .includes(fileRelation.file.id)

          // Set isFileHover to true if the file is not already related
          if (!isFileAlreadyRelated) {
            isFileHover.value = true
          }
        }
      } else {
        // Something else is being dragged, but we don't know what.
        // It might be an item from an external app (e.g. Whatsapp image).
        isFileHover.value = true
      }
    }
  }
}

function onDragLeave() {
  isFileHover.value = false
  invalidTypeHover.value = false
  multipleFilesHover.value = false
}

function onFileUploaded(fileRelation: FileRelation) {
  emit("fileUploaded", fileRelation)
}

function onFileLinked(fileRelation: FileRelation) {
  emit("fileLinked", fileRelation)
}

function throwNoFileBucketError() {
  throw new Error("No fileBucket prop passed and standalone mode is not enabled.")
}

watch(isUploading, (val) => {
  emit("isUploading", val)
})

watch(isFileHover, (val) => {
  emit("isFileHover", val)
})

watch(multipleFilesHover, (val) => {
  emit("multipleFilesHover", val)
})
</script>

<style scoped></style>
